import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";

import { Observable, throwError, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { CorrelationIdService, DffError, SessionIdService } from "@dffedb/util";
import { LogService } from "../log/log.service";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "../app-shell/app-shell-layout.token";
import { AppserverBrugerCredentialsRepository, AppserverInstallationCredentialsRepository } from "../credentials";
import { buildAbsoluteLoginUrlFromHref, isLoginRoutePath, isTeknikerContext, LOGIN_ROUTE } from "../util";

@Injectable()
export class EforsyningHttpInterceptor implements HttpInterceptor {
    private static hasShownUnauthorizedAlert = false;

    constructor(
        private readonly sessionIdService: SessionIdService,
        private readonly correlationIdService: CorrelationIdService,
        private readonly logService: LogService,
        private readonly brugerCredentialsRepository: AppserverBrugerCredentialsRepository,
        private readonly installationCredentialsRepository: AppserverInstallationCredentialsRepository,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly appShellLayout: AppShellLayoutOptions | null
    ) {}

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const startTime = new Date();
        const sessionId = this.sessionIdService.hentId();
        const correlationId = this.correlationIdService.genererId();

        if (this.erSammeOrigin(request.url)) {
            request = request.clone({
                setHeaders: {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    "X-Session-ID": sessionId,
                    "X-Correlation-ID": correlationId
                    /* eslint-enable @typescript-eslint/naming-convention */
                }
            });
        }

        request = this.udenHttpCacheForServicerapportOpgaver(request);

        this.safe(() => this.logStart(correlationId, request));
        return next.handle(request).pipe(
            tap((response) => {
                this.safe(() => this.logEnd(correlationId, startTime, request, response));
            }),
            catchError((error) => {
                this.safe(() => this.logError(correlationId, startTime, request, error));
                if (this.shouldIgnoreError(request)) {
                    return of(new HttpResponse({ status: 204, body: null }));
                }
                this.redirectOn401(error, request);
                // 401 logges allerede som Warning i logHttpError — undgå dobbelt Error via ErrorHandler.
                const isUnauthorized = error?.status === 401;
                return throwError(
                    new DffError(error.message, {
                        innerError: error,
                        correlationId,
                        suppressLog: isUnauthorized
                    })
                );
            })
        );
    }

    private safe(fn: () => void): void {
        try {
            fn();
        } catch (error) {
            console.error("DffHttpInterceptor:", error);
        }
    }

    private logStart(correlationId: string, request: HttpRequest<any>): void {
        this.logService.logHttpStart(correlationId, request);
    }

    private logEnd(correlationId: string, startTime: Date, request: HttpRequest<any>, response: HttpEvent<any>): void {
        if (response instanceof HttpResponse) {
            const duration = new Date().getTime() - startTime.getTime();
            this.logService.logHttpEnd(correlationId, duration, request, response);
        }
    }

    private logError(correlationId: string, startTime: Date, request: HttpRequest<any>, error: any): void {
        const duration = new Date().getTime() - startTime.getTime();
        this.logService.logHttpError(correlationId, duration, request, error);
    }

    private redirectOn401(error: any, request: HttpRequest<any>): void {
        const status = error?.status ?? error?.innerError?.status;
        if (status !== 401) return;

        // 401 på tilknyttede/eksterne installationer (anden eller tom cryptId) må ikke logge den primære session ud.
        if (!this.erPrimaerSessionUnauthorized(request)) {
            return;
        }

        try {
            sessionStorage.removeItem("credentials");
            sessionStorage.removeItem("user");
        } catch {
            console.warn("Credentials kunne ikke fjernes fra sessionStorage");
        }

        const href = window.location.href;
        const configured = this.appShellLayout?.teknikerLoginRoute?.trim();
        const teknikerHash = isTeknikerContext() && configured ? (configured.startsWith("/") ? configured : `/${configured}`) : LOGIN_ROUTE;
        const target = buildAbsoluteLoginUrlFromHref(href, teknikerHash);

        const currentPath = (window.location.hash || "").replace(/^#/, "");
        if (!isLoginRoutePath(currentPath)) {
            this.showUnauthorizedAlertOnce();
            window.location.href = target;
        }
    }

    /**
     * Kun 401 på den primære session skal logge ud.
     * Tilknytninger autentificerer via system/login/.../id/{cryptId} og getsecuritytoken (uden query-id).
     * Fejlende tilknytninger kan også ramme getebrugerinfo uden (gyldig) cryptId — det må ikke logge primær ud.
     */
    private erPrimaerSessionUnauthorized(request: HttpRequest<any>): boolean {
        const url = request?.url || "";
        const primaerCryptId = this.hentPrimaerCryptId();
        const requestCryptId = this.hentCryptIdFraUrl(url);

        if (this.harCryptIdParameter(url)) {
            // Tom id= eller anden cryptId end primær → tilknytning/fejlende værk, ikke logout.
            if (!requestCryptId) {
                return false;
            }
            if (!primaerCryptId) {
                return true;
            }
            return requestCryptId === primaerCryptId;
        }

        // Auth-probes for tilknytninger (getsecuritytoken / login uden genkendt id) må ikke rydde primær session.
        if (this.erAppserverAuthProbe(url)) {
            return false;
        }

        // Appserver data-kald uden cryptId i URL (fx getebrugerinfo efter tom credentials) er tilknytning/race — ikke primær logout.
        // Primær appserver-session sender altid cryptId når den er logget ind.
        if (this.erAppserverApiKald(url)) {
            return false;
        }

        // Øvrige 401 uden cryptId (fx Umbraco) behandles som primær session.
        return true;
    }

    private erAppserverAuthProbe(url: string): boolean {
        const lower = (url || "").toLowerCase();
        return lower.includes("/system/getsecuritytoken") || lower.includes("/system/login/");
    }

    private erAppserverApiKald(url: string): boolean {
        const lower = (url || "").toLowerCase();
        // Remote appserver eller typiske appserver data-endpoints (også relative/proxy-URL'er).
        return lower.includes("apiapp.dff-edb.dk") || lower.includes("/api/getebrugerinfo");
    }

    private harCryptIdParameter(url: string): boolean {
        // Query ?id= / &id= eller path-segment /id/{cryptId} (appserver login + getebrugerinfo).
        return /[?&]id=/i.test(url) || /\/id\//i.test(url);
    }

    private hentPrimaerCryptId(): string {
        try {
            return this.brugerCredentialsRepository.hent()?.cryptId || this.installationCredentialsRepository.hent()?.cryptId || "";
        } catch {
            return "";
        }
    }

    private hentCryptIdFraUrl(url: string): string {
        try {
            const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost");
            // searchParams.get("id") returnerer "" for tom id= — det er en eksplicit parameter.
            if (parsed.searchParams.has("id")) {
                return (parsed.searchParams.get("id") || "").trim();
            }

            const pathMatch = parsed.pathname.match(/\/id\/([^/]*)/i);
            return pathMatch?.[1]?.trim() || "";
        } catch {
            const queryMatch = url.match(/[?&]id=([^&]*)/i);
            if (queryMatch) {
                return decodeURIComponent(queryMatch[1] || "").trim();
            }
            const pathMatch = url.match(/\/id\/([^/?#]*)/i);
            return pathMatch?.[1]?.trim() || "";
        }
    }

    private showUnauthorizedAlertOnce(): void {
        if (EforsyningHttpInterceptor.hasShownUnauthorizedAlert) {
            return;
        }

        EforsyningHttpInterceptor.hasShownUnauthorizedAlert = true;
        window.alert("Din session er udløbet, og dine loginoplysninger er ikke længere aktive. Du bliver nu sendt til login.");
    }

    private udenHttpCacheForServicerapportOpgaver(request: HttpRequest<any>): HttpRequest<any> {
        if (request.method !== "GET" || !request.url.includes("HentServicerapportOpgaver")) {
            return request;
        }

        const separator = request.url.includes("?") ? "&" : "?";
        return request.clone({
            url: `${request.url}${separator}_=${Date.now()}`,
            setHeaders: {
                /* eslint-disable @typescript-eslint/naming-convention */
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache"
                /* eslint-enable @typescript-eslint/naming-convention */
            }
        });
    }

    private shouldIgnoreError(request: HttpRequest<any>): boolean {
        if (!isTeknikerContext()) {
            return false;
        }

        const url = request?.url?.toLowerCase() || "";
        return url.includes("/api/notifications/documents");
    }

    private erSammeOrigin(url: string): boolean {
        if (!/^https?:\/\//i.test(url)) {
            return true;
        }
        try {
            if (typeof window === "undefined") {
                return false;
            }
            return new URL(url).origin === window.location.origin;
        } catch {
            return false;
        }
    }
}
